import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useHistory, useLocation } from 'react-router-dom'
import {
  IconArrowLeft,
  IconCode,
  IconDatabaseImport,
  IconDownload,
  IconPencil,
  IconPhoneCall,
  IconUpload,
  IconVersions,
  IconWorld,
} from '@tabler/icons-react'
import queryString from 'query-string'

import { Menu } from '@/components/navigation/Menu'
import { Button } from '@/components/buttons/Button'
import { CopyButton } from '@/components/buttons/CopyButton'
import { ActionsDropdown } from '@/components/dropdowns/ActionsDropdown'
import { getSelectedAgent } from '@/reducers/voiceAgents/voiceAgents.helpers'
import { exportOverrides } from '@/reducers/voiceAgents/voiceAgents.actions'

import { countOpenRequests } from './changeRequestHelpers'
import { useAgentDisplayName } from './hooks/useAgentDisplayName'
import { useAgentIdentifiers } from './hooks/useAgentIdentifiers'
import { useWebCallContext } from './hooks/WebCallContext'
import { useAgentOrb } from './hooks/useAgentOrb'
import { conicWash, orbRing } from './orbPalettes'
import YamlModal from './components/YamlModal'
import ImportOverridesModal from './components/ImportOverridesModal'
import DemoCallModal from './components/DemoCallModal'
import ReseedConfigModal from './components/ReseedConfigModal'
import ConfigGenerationsModal from './components/ConfigGenerationsModal'
import RenameAgentModal from './components/RenameAgentModal'

// Top-level page tabs, in design-mock order. `key` is the stable `?tab=` URL
// param (and the VoiceAgentsPage switch value) — it stays `change-requests`
// even though the label is "Comments", so routing and testids don't churn.
export const PAGE_TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'call-flow', label: 'Call Flow' },
  { key: 'change-requests', label: 'Comments' },
  // Admin org (org 1) only — filtered out of the header for other orgs.
  { key: 'credentials', label: 'Credentials' },
]

// Tabs restricted to the Balto admin org (org 1) for now (AVR-463).
const ADMIN_ONLY_TABS = new Set(['credentials'])

export const DEFAULT_PAGE_TAB = 'overview'

// Resolve the active tab from the URL `?tab=` query param, falling back to
// the default when the param is missing or unrecognized. Exported so the page
// and the header share one source of truth.
export const resolvePageTab = (search) => {
  const { tab } = queryString.parse(search)
  return PAGE_TABS.some((entry) => entry.key === tab) ? tab : DEFAULT_PAGE_TAB
}

const AgentHeader = () => {
  const dispatch = useDispatch()
  const [yamlModalOpen, setYamlModalOpen] = useState(false)
  const [importModalOpen, setImportModalOpen] = useState(false)
  const [demoCallModalOpen, setDemoCallModalOpen] = useState(false)
  const [generationsModalOpen, setGenerationsModalOpen] = useState(false)
  const [reseedModalOpen, setReseedModalOpen] = useState(false)
  const [renameModalOpen, setRenameModalOpen] = useState(false)
  const agent = useSelector((state) => getSelectedAgent(state.voiceAgents))
  // Gates the promotion overflow menu only — calling is no longer admin-only.
  // Keyed on the user's *true* org, which stays 1 while a Balto admin views
  // another tenant, so the tools follow the person rather than the org on
  // screen.
  const isBaltoAdmin = useSelector((state) => state.currentUser.own_organization_id === 1)
  const exportLoading = useSelector((state) => state.voiceAgents.loading?.exportOverrides)
  const commentCount = useSelector((state) =>
    countOpenRequests(state.voiceAgents.changeRequests.items)
  )
  const isOrg1 = useSelector((state) => state.currentUser.own_organization_id) === 1
  const history = useHistory()
  const location = useLocation()
  const activeTab = resolvePageTab(location.search)
  // Header pill shows the Cartesia agent id (what the list page's "Cartesia
  // agent ID" column shows); the PE id is the fallback when the provisioning
  // record can't be resolved (legacy /:agentId route).
  const { cartesiaAgentId, voiceAgentId, organizationId } = useAgentIdentifiers()
  const { displayName, ownerName } = useAgentDisplayName()
  const { openScreen } = useWebCallContext()
  const orbPalette = useAgentOrb()
  const headerAgentId = cartesiaAgentId || agent?.id
  const visibleTabs = PAGE_TABS.filter((tab) => !ADMIN_ONLY_TABS.has(tab.key) || isOrg1)

  // Renaming needs the voice_agents row to address; the legacy /:agentId route
  // resolves no provisioning record, so the pencil hides rather than opening a
  // form that could not save.
  const canRename = Boolean(voiceAgentId)

  // Preserve the org scope (`?org=`) threaded in from the list so going back
  // returns to the same org's agents rather than the "Select an organization"
  // prompt.
  const { org } = queryString.parse(location.search)
  const backTo = org ? `/voice-agents?${queryString.stringify({ org })}` : '/voice-agents'

  // Overflow-menu entries. All of them act on the agent as a whole, so they are
  // available from every tab — the menu is the same wherever you are, and each
  // action opens a modal that carries its own context.
  const adminActions = [
    {
      label: 'Reseed config',
      icon: <IconDatabaseImport />,
      testId: 'reseed-config-trigger',
      onClick: () => setReseedModalOpen(true),
      disabled: !agent?.id,
    },
    {
      label: 'Generations',
      icon: <IconVersions />,
      testId: 'config-generations-trigger',
      onClick: () => setGenerationsModalOpen(true),
      disabled: !agent?.id,
    },
    {
      label: 'Prompt config YAML',
      icon: <IconCode />,
      testId: 'yaml-modal-trigger',
      onClick: () => setYamlModalOpen(true),
      hasSeparator: true,
    },
    {
      label: 'Export overrides',
      icon: <IconDownload />,
      testId: 'export-overrides-button',
      onClick: () => dispatch(exportOverrides(agent?.id)),
      disabled: !agent?.id || exportLoading,
    },
    {
      label: 'Import overrides',
      icon: <IconUpload />,
      testId: 'import-overrides-trigger',
      onClick: () => setImportModalOpen(true),
      disabled: !agent?.id,
    },
  ]

  const handleTabClick = (tabKey) => {
    if (tabKey === activeTab) {
      return
    }
    const next = { ...queryString.parse(location.search), tab: tabKey }
    history.replace({ pathname: location.pathname, search: queryString.stringify(next) })
  }

  // Opens the call view; it does not dial. The view is where you decide to
  // call, and landing in one that is already ringing gives you nothing to
  // decide — the same reason a phone app opens on a keypad, not a call.
  const handleWebCall = () => {
    openScreen()
  }

  const callActions = [
    {
      // "Inbound" from the agent's point of view, which is the one that
      // matters here: you are the caller reaching it, the same as a customer
      // dialling in. "Web call" described the transport instead, which told
      // you nothing about what the agent would do.
      label: 'Inbound call',
      icon: <IconWorld />,
      testId: 'call-agent-web',
      onClick: handleWebCall,
      disabled: !agent?.id,
    },
    {
      // Outbound stays a modal: it is a form you fill in — a number and
      // optional metadata — and then it is over. Nothing to watch on this
      // screen, because that conversation happens on someone's phone.
      label: 'Outbound call',
      icon: <IconPhoneCall />,
      testId: 'call-agent-phone',
      onClick: () => setDemoCallModalOpen(true),
      disabled: !agent?.id,
    },
  ]

  return (
    <header className="voice-agents__header" data-testid="agent-header">
      <div className="voice-agents__header-top">
        <div className="voice-agents__header-identity">
          <Link
            to={backTo}
            className="balto-button default small square"
            data-testid="agent-header-back"
            aria-label="Back to AI Agents"
          >
            <IconArrowLeft size={16} />
          </Link>
          {/* The agent's orb and name, as one unit. Same colour the agent
              carries on its card and in the call dock — the blob is how you
              recognise it, so it has to be the same everywhere. Grouped rather
              than dropped into the row on its own so it sits tight against the
              title it labels, and gated on the title so the row never shows a
              blob labelling nothing while the config loads. */}
          {displayName && (
            <div className="voice-agents__header-name">
              <span
                className="voice-agents__header-orb"
                style={{ boxShadow: orbRing(orbPalette) }}
                data-testid="agent-header-orb"
                aria-hidden="true"
              >
                <span
                  className="voice-agents__header-orb-wash"
                  style={{ background: conicWash(orbPalette, 32) }}
                />
              </span>
              <h2 className="voice-agents__header-title" data-testid="agent-header-title">
                {displayName}
              </h2>
              {/* Rename sits on the name itself rather than in the overflow
                  menu: it is the one agent-wide action a customer has, and the
                  overflow menu is Balto-admin only. */}
              {canRename && (
                <Button
                  basic
                  small
                  square
                  icon={<IconPencil />}
                  tooltip="Rename agent"
                  aria-label={`Rename ${displayName}`}
                  className="voice-agents__header-rename"
                  onClick={() => setRenameModalOpen(true)}
                  data-testid="agent-header-rename"
                />
              )}
            </div>
          )}
          {headerAgentId && (
            <span className="voice-agents__agent-id-pill" data-testid="agent-header-id">
              <span className="voice-agents__agent-id-value">{headerAgentId}</span>
              <CopyButton
                content={headerAgentId}
                iconOnly
                text
                showTooltip
                tooltipContent="Copy agent ID"
              />
            </span>
          )}
        </div>
        <div className="voice-agents__header-actions">
          {/* Talking to the agent is for anyone who can be on this page.
              Reaching it already means passing the route's `togoEditorRoute`
              gate — `togo_editor` on the user and `togo_enabled` on the org,
              with Balto admin bypassing — which is exactly the audience
              allowed to call. Recomputing that predicate here would be a
              second copy of the same rule, free to drift from the router's.

              This gate is not the security boundary. The voice-agents API
              re-checks Studio permission and adds the part a browser cannot be
              trusted for — that the caller can reach the agent's owning org —
              so a direct API call gets the same answer as the UI. Named no more
              specifically than that on purpose: it lives in another repo and
              would go stale here. */}
          <ActionsDropdown
            triggerLabel="Talk to Togo"
            actions={callActions}
            dataTestId="call-agent-menu"
            buttonProps={{ primary: true, 'aria-label': 'Talk to Togo' }}
          />

          {/* The overflow menu stays Balto-admin only: reseed, generations,
              prompt-config YAML and override export/import are promotion
              plumbing, not something a customer should see or use.

              Keyed on `own_organization_id`, so a Balto admin keeps these tools
              while viewing another tenant. The list page's "Create agent" CTA
              and the `/voice-agents/create` guard key on the *active* org
              instead, because provisioning targets whichever org you're in — a
              different question, and the CTA matches its own route guard, so
              neither can contradict the other. */}
          {isBaltoAdmin && (
            <ActionsDropdown
              actions={adminActions}
              dataTestId="agent-admin-menu"
              buttonProps={{ 'aria-label': 'Agent admin actions' }}
            />
          )}
        </div>
      </div>
      <Menu
        menuStyle="bar"
        items={visibleTabs.map((tab) => ({
          accessor: tab.key,
          label: tab.label,
          active: tab.key === activeTab,
          onClick: () => handleTabClick(tab.key),
          dataTestId: `agent-header-tab-${tab.key}`,
          badge: tab.key === 'change-requests' && commentCount > 0 ? commentCount : undefined,
          badgeProps: { 'data-testid': 'agent-comments-count' },
        }))}
        data-testid="agent-header-tabs"
      />
      <YamlModal show={yamlModalOpen} onClose={() => setYamlModalOpen(false)} />
      <ImportOverridesModal show={importModalOpen} onClose={() => setImportModalOpen(false)} />
      <DemoCallModal show={demoCallModalOpen} onClose={() => setDemoCallModalOpen(false)} />
      <ReseedConfigModal show={reseedModalOpen} onClose={() => setReseedModalOpen(false)} />
      <ConfigGenerationsModal
        show={generationsModalOpen}
        onClose={() => setGenerationsModalOpen(false)}
      />
      <RenameAgentModal
        show={renameModalOpen}
        onClose={() => setRenameModalOpen(false)}
        voiceAgentId={voiceAgentId}
        currentName={ownerName}
        orgId={organizationId}
      />
    </header>
  )
}

export default AgentHeader
